import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  listFiles,
  getFile,
  uploadFile,
  deleteDataset,
  loadHuggingFaceDataset,
} from "../services/datasetService";
import { Parser } from "@json2csv/plainjs";

import { useUnsavedChanges } from "../components/editor/UnsavedChangesContext";
import { checkExtension, validateContent } from "../utils/datasetValidation";

// Escape a single field for CSV output (RFC 4180): wrap in quotes when it
// contains a comma, quote, or newline, and double any embedded quotes.

// Detect the column delimiter used by a plain text dataset by sampling the
// first few rows. Checks common single-character delimiters, then falls back
// to runs of whitespace (the usual layout for space/tab-aligned .txt data).
// Returns null when no consistent multi-column delimiter is found.

// Convert a delimited plain text dataset into CSV so it loads and trains as a
// proper table instead of one column per line. Text with no detectable
// delimiter is returned as a single-column CSV (each line becomes one cell).

const readExcelFileAsCsv = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array" });

  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    throw new Error("The Excel file does not contain any sheets.");
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const csvContent = XLSX.utils.sheet_to_csv(worksheet);

  if (!csvContent || !csvContent.trim()) {
    throw new Error("The selected Excel sheet is empty.");
  }

  return csvContent;
};

const readJsonFileAsCsv = async (file) => {
  const fileContent = await file.text();
  const jsonData = JSON.parse(fileContent);

  if (!Array.isArray(jsonData)) {
    throw new Error("JSON dataset must be an array of objects.");
  }

  const flattenedData = jsonData.map((row) => {
    const flattenedRow = {};

    Object.entries(row).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        flattenedRow[key] = value.join(" | ");
      } else if (typeof value === "object" && value !== null) {
        flattenedRow[key] = JSON.stringify(value);
      } else {
        flattenedRow[key] = value;
      }
    });

    return flattenedRow;
  });

  const parser = new Parser();
  const csvContent = parser.parse(flattenedData);

  if (!csvContent || !csvContent.trim()) {
    throw new Error("The JSON file could not be converted into CSV.");
  }

  return csvContent;
};

const processDatasetFile = async (file, selectedDatasetType) => {
  if (selectedDatasetType === "excel") {
    return {
      content: await readExcelFileAsCsv(file),
      fileName: file.name.replace(/\.(xlsx|xls)$/i, ".csv"),
      originalName: file.name,
      fileType: "csv",
    };
  }

  if (selectedDatasetType === "json") {
    return {
      content: await readJsonFileAsCsv(file),
      fileName: file.name.replace(/\.json$/i, ".csv"),
      originalName: file.name,
      fileType: "csv",
    };
  }

  const fileContent = await file.text();

  return {
    content: fileContent,
    fileName: file.name,
    originalName: file.name,
    fileType: selectedDatasetType,
  };
};

/**
 * Custom hook to manage dataset and file upload state/logic.
 * Handles:
 * - Uploaded file state, local, cloud, and Hugging Face datasets
 * - CSV preview data
 * - Cloud file browser state
 * - File upload/download/delete operations
 * - Integration with unsaved changes context
 */
export const useDataset = (codebaseId, userId, onUploadSuccess) => {
  // Core dataset state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);

  // Cloud file browser state
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [bucketFiles, setBucketFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [loadingFiles, setLoadingFiles] = useState(false);

  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Fetch files from cloud storage
  const fetchBucketFiles = async (path) => {
    try {
      setLoadingFiles(true);

      const filesData = await listFiles(userId);
      setBucketFiles(filesData.files || []);

      setLoadingFiles(false);
    } catch (error) {
      console.error("Error fetching bucket files:", error);
      setLoadingFiles(false);
      Swal.fire("Error", "Could not load cloud files", "error");
    }
  };

  // Fetch files when dialog opens
  useEffect(() => {
    if (showFileDialog) {
      fetchBucketFiles(currentPath);
    }
  }, [showFileDialog, currentPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse CSV file content and create preview table
  const parseCSVPreview = (fileContent) => {
    if (!fileContent) {
      setPreviewData([]);
      return;
    }

    const lines = fileContent.trim().split("\n");

    if (lines.length === 0) {
      setPreviewData([]);
      return;
    }

    const rows = lines.slice(0, 10).map((line) => line.split(","));
    setPreviewData(rows);
  };

  // Handle local dataset upload with extension and content validation
  const handleLocalFileUpload = async (event, selectedDatasetType = "csv") => {
    const file = event.target.files[0];

    // Reset the input so the same file can be re-selected after a rejection
    event.target.value = "";

    if (!file) return;

    const extensionCheck = checkExtension(file.name, selectedDatasetType);

    if (!extensionCheck.valid) {
      Swal.fire({
        icon: "error",
        title: "Invalid File Type",
        text: `"${file.name}" does not match the selected dataset type. Expected: ${extensionCheck.expected.join(
          " or "
        )}`,
        confirmButtonText: "Close",
      });
      return;
    }

    let contentCheck;

    try {
      contentCheck = await validateContent(file, selectedDatasetType);
    } catch (error) {
      console.error("Error validating file:", error);
      contentCheck = {
        valid: false,
        details: ["File could not be read."],
      };
    }

    if (!contentCheck.valid) {
      Swal.fire({
        icon: "error",
        title: "Corrupted Dataset",
        text: `"${file.name}" could not be validated. ${contentCheck.details.join(
          " "
        )}`,
        confirmButtonText: "Close",
      });
      return;
    }

    let processedDataset;

    try {
      processedDataset = await processDatasetFile(file, selectedDatasetType);
    } catch (error) {
      console.error("Dataset processing failed:", error);

      Swal.fire({
        icon: "error",
        title: "Dataset Processing Failed",
        text:
          error.message ||
          "The selected dataset could not be processed. Please try another file.",
        confirmButtonText: "Close",
      });

      return;
    }

    setUploadedFile({
      name: processedDataset.fileName,
      originalName: processedDataset.originalName,
      content: processedDataset.content,
      fileType: processedDataset.fileType,
      sourceType: selectedDatasetType,
    });

    parseCSVPreview(processedDataset.content);

    localStorage.setItem(`filepath_${codebaseId}`, processedDataset.fileName);
    setHasUnsavedChanges(true);

    Swal.fire({
      icon: "success",
      title: "File Read Locally",
      text:
        selectedDatasetType === "excel"
          ? "Excel file converted to CSV preview. Click 'Save to Cloud' to finalize."
          : selectedDatasetType === "json"
          ? "JSON file converted to CSV preview. Click 'Save to Cloud' to finalize."
          : "Preview loaded. Click 'Save to Cloud' to finalize.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  // Handle Hugging Face dataset loading
  const handleHuggingFaceDatasetLoad = async (datasetUrl) => {
    if (!datasetUrl || !datasetUrl.trim()) {
      Swal.fire(
        "Dataset Required",
        "Please enter a Hugging Face dataset URL or dataset ID.",
        "warning"
      );
      return;
    }

    if (!userId) {
      Swal.fire(
        "Login Required",
        "User ID is missing. Please log in again.",
        "error"
      );
      return;
    }

    try {
      setLoadingFiles(true);

      const result = await loadHuggingFaceDataset(
        datasetUrl.trim(),
        userId,
        1000
      );

      setUploadedFile({
        name: result.filename,
        content: result.content,
        dataset_id: result.datasetId,
        fileType: "huggingface",
      });

      parseCSVPreview(result.content);
      localStorage.setItem(`filepath_${codebaseId}`, result.filename);

      const sessionIds = JSON.parse(
        sessionStorage.getItem("session_dataset_ids") || "[]"
      );

      if (!sessionIds.includes(result.datasetId)) {
        sessionIds.push(result.datasetId);
      }

      sessionStorage.setItem(
        "session_dataset_ids",
        JSON.stringify(sessionIds)
      );

      setHasUnsavedChanges(false);
      setLoadingFiles(false);

      Swal.fire({
        icon: "success",
        title: "Hugging Face Dataset Loaded",
        text: `${result.datasetName} loaded with ${result.rowCount} rows and ${result.columnCount} columns.`,
        timer: 2500,
        showConfirmButton: false,
      });

      return result;
    } catch (error) {
      setLoadingFiles(false);
      console.error("Error loading Hugging Face dataset:", error);

      Swal.fire(
        "Could Not Load Dataset",
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Please check the Hugging Face dataset name or URL.",
        "error"
      );
    }
  };

  // Handle Python file upload - loads into first section of editor
  const handlePythonUpload = (event) => {
    const file = event.target.files[0];

    // Reset input so same file can be selected again
    event.target.value = "";

    if (!file) return;

    if (!file.name.endsWith(".py")) {
      Swal.fire("Wrong File Type", "Please upload a .py file", "error");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const fileContent = e.target.result;

      if (onUploadSuccess) {
        onUploadSuccess({
          name: file.name,
          content: fileContent,
        });
      }

      Swal.fire({
        icon: "success",
        title: "Python File Loaded",
        text: `${file.name} loaded into the editor.`,
        timer: 2000,
        showConfirmButton: false,
      });
    };

    reader.readAsText(file);
  };

  // Select and load file from cloud storage
  const handleCloudFileSelect = async (datasetId, fileName) => {
    try {
      setLoadingFiles(true);

      const fileData = await getFile(datasetId, userId);
      const { content } = fileData;

      setUploadedFile({
        name: fileName,
        content,
        dataset_id: datasetId,
      });

      parseCSVPreview(content);
      localStorage.setItem(`filepath_${codebaseId}`, fileName);
      setShowFileDialog(false);
      setLoadingFiles(false);

      Swal.fire({
        icon: "success",
        title: "File Loaded",
        text: `${fileName} loaded from cloud`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error loading cloud file:", error);
      setLoadingFiles(false);
      Swal.fire("Error", "Could not load file from cloud", "error");
    }
  };

  // Upload file to cloud storage
  const uploadToCloud = async () => {
    if (!uploadedFile || !uploadedFile.content) {
      Swal.fire("No Data", "Please select a file locally first.", "warning");
      return;
    }

    try {
      setLoadingFiles(true);

      const uploadResult = await uploadFile(
        uploadedFile.content,
        uploadedFile.name,
        userId
      );

      setLoadingFiles(false);

      if (uploadResult && uploadResult.datasetId) {
        setUploadedFile((prev) => ({
          ...prev,
          dataset_id: uploadResult.datasetId,
        }));

        const sessionIds = JSON.parse(
          sessionStorage.getItem("session_dataset_ids") || "[]"
        );

        if (!sessionIds.includes(uploadResult.datasetId)) {
          sessionIds.push(uploadResult.datasetId);
        }

        sessionStorage.setItem(
          "session_dataset_ids",
          JSON.stringify(sessionIds)
        );

        setHasUnsavedChanges(false);

        Swal.fire({
          icon: "success",
          title: "Saved to Cloud",
          text: "Uploaded successfully.",
          timer: 2000,
          showConfirmButton: false,
        });

        if (showFileDialog) {
          fetchBucketFiles(currentPath);
        }
      }
    } catch (error) {
      setLoadingFiles(false);
      console.error("Pipeline Error:", error);

      Swal.fire(
        "Error",
        "The database rejected the upload. Check your session.",
        "error"
      );
    }
  };

  // Delete a dataset from cloud storage
  const handleDeleteDataset = async (datasetId, fileName) => {
    const result = await Swal.fire({
      title: "Delete Dataset",
      text: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteDataset(datasetId, userId);

        setBucketFiles((prev) =>
          prev.filter((file) => file.datasetId !== datasetId)
        );

        const sessionIds = JSON.parse(
          sessionStorage.getItem("session_dataset_ids") || "[]"
        );

        sessionStorage.setItem(
          "session_dataset_ids",
          JSON.stringify(sessionIds.filter((id) => id !== datasetId))
        );

        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Dataset deleted successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error deleting dataset:", error);
        Swal.fire("Error", "Could not delete dataset.", "error");
      }
    }
  };

  // Navigate to folder in cloud browser
  const navigateFolder = (folderPath) => {
    setCurrentPath(folderPath);
  };

  // Clear uploaded file
  const clearUploadedFile = () => {
    setUploadedFile(null);
    setPreviewData([]);
    localStorage.removeItem(`filepath_${codebaseId}`);
  };

  return {
    // State
    uploadedFile,
    previewData,
    showFileDialog,
    bucketFiles,
    currentPath,
    loadingFiles,

    // Setters
    setUploadedFile,
    setPreviewData,
    setShowFileDialog,
    setBucketFiles,
    setCurrentPath,
    setLoadingFiles,

    // Methods
    handleLocalFileUpload,
    handleHuggingFaceDatasetLoad,
    handlePythonUpload,
    handleCloudFileSelect,
    uploadToCloud,
    handleDeleteDataset,
    navigateFolder,
    fetchBucketFiles,
    parseCSVPreview,
    clearUploadedFile,
  };
};